// <s-bike-fit> — compares the rider's saved bike (localStorage, set by the Shop by Bike finder) with this
// product's listed compatible models. It never infers fitment: a bike counts as listed only when an entry names
// the same brand (in the entry or the product's compatible brands) and the same model (known shorthand like LBX counts), optionally followed
// by the saved generation or a year range. Anything else reads as "not listed" or "not confirmed".

const BIKE_KEY = 'pl-my-bike';
const CHANGE_EVENT = 'bike-finder:change';

const clean = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Shorthand model names riders and listings use for the same bike. Each alias is rewritten to the finder's
// full model name before comparing, so a product listed for "LBX" matches a saved "Light Bee X".
const ALIASES = [
  [/\b(lbx|light bee x|lightbee x)\b/g, 'light bee x'],
  [/\b(lbs|light bee s|lightbee s)\b/g, 'light bee s'],
  [/\b(ub|ultra bee|ultrabee)\b/g, 'ultra bee'],
  [/\b(storm bee|stormbee)\b/g, 'storm bee'],
  [/\b(hyper bee|hyperbee)\b/g, 'hyper bee'],
  [/\b(surron|sur ron)\b/g, 'sur ron'],
];

const norm = (s) => {
  let out = clean(s);
  for (const [pattern, canonical] of ALIASES) out = out.replace(pattern, canonical);
  return out;
};
const YEARS = /^(\d{2,4})(\s+\d{2,4})*(\s+(and\s+)?(up|newer|later|on))?$/;

// Translated messages arrive HTML-escaped (e.g. isn&#39;t). Decode them to plain text so riders see real
// apostrophes and ampersands instead of entity codes. DOMParser never runs scripts, and the result is set
// with textContent, so this stays safe.
function decodeEntities(str) {
  let out = str;
  for (let i = 0; i < 2 && /&(#\d+|#x[0-9a-f]+|[a-z]+);/i.test(out); i++) {
    out = new DOMParser().parseFromString(out, 'text/html').documentElement.textContent || '';
  }
  return out;
}

function readBike() {
  try {
    const bike = JSON.parse(localStorage.getItem(BIKE_KEY) || 'null');
    return bike && bike.brand ? bike : null;
  } catch {
    return null;
  }
}

class SBikeFit extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    try {
      this.models = JSON.parse(this.dataset.models || '[]');
      this.brands = JSON.parse(this.dataset.brands || '[]');
    } catch {
      this.models = [];
      this.brands = [];
    }
    if (!Array.isArray(this.models)) this.models = [];
    if (!Array.isArray(this.brands)) this.brands = [];
    this.render();
    window.addEventListener(CHANGE_EVENT, () => this.render(), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #state(bike) {
    if (!bike) return 'none';
    if (this.models.length === 0) return 'unknown';
    const brand = norm(bike.brand);
    const model = norm(bike.model);
    const gen = norm(bike.gen);
    if (!model) return 'brand-only';
    const brandListed = this.brands.some((b) => norm(b) === brand);

    const listed = this.models.some((entry) => {
      let rest = norm(entry);
      if (rest === brand || rest.startsWith(`${brand} `)) {
        rest = rest.slice(brand.length).trim();
      } else if (!brandListed) {
        return false;
      }
      if (rest === model) return true;
      if (!rest.startsWith(`${model} `)) return false;
      const tail = rest.slice(model.length).trim();
      return (gen && tail === gen) || YEARS.test(tail);
    });
    if (!listed) return 'not-listed';
    return this.dataset.verified === 'true' ? 'fits' : 'listed';
  }

  render() {
    const bike = readBike();
    const state = this.#state(bike);
    const label = bike ? [bike.brand, bike.model, bike.gen].filter(Boolean).join(' ') : '';
    this.dataset.state = state;
    this.querySelectorAll('[data-fit-state]').forEach((el) => {
      const on = el.dataset.fitState.split(' ').includes(state);
      el.hidden = !on;
      if (!on) return;
      el.querySelectorAll('[data-fit-text]').forEach((t) => {
        t.textContent = decodeEntities(t.dataset.template || '').replace('[bike]', label);
      });
    });
  }
}

if (!customElements.get('s-bike-fit')) customElements.define('s-bike-fit', SBikeFit);
